import { BaseChartPresetHandler } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import { OfPieChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/of-pie-chart';
import {
    PIE_CHART_SERIES_CATEGORIES_PRESET,
    PIE_CHART_SERIES_NAME_PRESET,
    PIE_CHART_VALUES_PRESET,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/pie';
import { PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import type {
    ModifyPlotAreaProps,
    SeriesInitialProps,
    SeriesPresetPoint,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import { IOfPieChart } from '../../../Interface/chart-v3/sub-chart';
import { STOfPieType, SubChartType } from '../../../Interface/chart-v3/sdk-model.interface';
import { ChartSeries } from '../../sub-chart/series-v2';
import { IChartAxis, IChartSpace } from '../../../Interface/chart-v3/chart-properties.interface';
import { IBaseSubChart } from '../../../Interface/chart-v3/sub-chart/base-sub-chart.interface';
import { LineJoin } from '@tencent/alloy-renderer';
import { PieChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2/pie-chart-series';
import { ILineCap } from '../../../property/border/border.interface';
import { getThemeColorValueByIndex } from '../../../property/chart/theme';
import { TDColor } from '../../../property/color/td-color';
import { STSchemeColorVal } from '@tencent/slide-sdk-model';

export class OfPieChartPresetHandler extends BaseChartPresetHandler {
    get ChartSeries() {
        return PieChartSeries;
    }

    get SubChart() {
        return OfPieChart;
    }

    protected defaultChartDataPreset = {
        categories: PIE_CHART_SERIES_CATEGORIES_PRESET,
        values: PIE_CHART_VALUES_PRESET,
    };

    public createChartModel(
        type: PresetChartType,
        chartSpace: IChartSpace,
        initialProps?: SeriesInitialProps,
    ): IOfPieChart {
        const subChart = super.createChartModel(type, chartSpace, initialProps) as IOfPieChart;

        this.setOfPieType(subChart, type);

        return subChart;
    }

    public modifySubChartType(
        IBaseSubCharts: IBaseSubChart[],
        type: PresetChartType,
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined,
        chartSpace: IChartSpace,
    ) {
        const newSubChart = super.modifySubChartType(
            IBaseSubCharts, type,
            getChartPresetHandlerByChartBaseType, chartSpace,
        );

        // @ts-ignore
        newSubChart.axisIds = undefined;

        return newSubChart;
    }

    public modifyPlotArea(): ModifyPlotAreaProps {
        return {
            removeProps: {
                categoryAxes: null,
                valueAxes: null,
                dateAxes: null,
            },
        };
    }

    public generateAxesPreset(subChart: IBaseSubChart): {
        categoryAxes?: IChartAxis[];
        valueAxes?: IChartAxis[];
        dataAxes?: IChartAxis[];
    } {
        return {};
    }

    protected generateSeriesPreset(
        ChartSeriesConstructor: typeof ChartSeries,
        valuesPreset: SeriesInitialProps['valuesPreset'],
        categoriesPreset: SeriesInitialProps['categoriesPreset'],
        seriesNamesPreset: SeriesInitialProps['seriesNamesPreset'],
        showDataLabels?: boolean,
        colorPaletteId = '10',
    ) {
        const baseSeriesPreset = super
            .generateSeriesPreset(
                ChartSeriesConstructor,
                valuesPreset,
                categoriesPreset,
                seriesNamesPreset,
                showDataLabels,
            );

        // 将原本在generatePresetSeriesInitialProps中添加的dataPoint和otherDPt转移到这里修改，这里面向uiModel（之前面向sdk）
        baseSeriesPreset.forEach((ser) => {
            ser.dataList.forEach((point, index) => {
                const color = getThemeColorValueByIndex(index, colorPaletteId, ser.dataList.length);
                point.dataPoint = {
                    props: {
                        style: {
                            fillBackground: {
                                solidFill: {
                                    color,
                                },
                            },
                            border: {
                                width: 3,
                                tdColor: new TDColor({
                                    themeKey: STSchemeColorVal.lt1,
                                }),
                            },
                        },
                    },
                };
            });

            ser.otherDPt = {
                props: {
                    style: {
                        fillBackground: {
                            solidFill: {
                                color: getThemeColorValueByIndex(
                                    ser.dataList.length,
                                    colorPaletteId, ser.dataList.length,
                                ),
                            },
                        },
                        border: {
                            width: 3,
                            tdColor: new TDColor({
                                themeKey: STSchemeColorVal.lt1,
                            }),
                        },
                    },
                },
            };
        });

        return baseSeriesPreset;
    }

    protected generatePresetSeriesInitialProps(
        seriesIndex: number,
        points: Array<SeriesPresetPoint>,
        seriesName?: string,
        showDataLabels = false,
    ) {
        const baseProps = super.generatePresetSeriesInitialProps(seriesIndex, points, seriesName, showDataLabels);

        return {
            ...baseProps,
            seriesText: {
                value: seriesName ?? PIE_CHART_SERIES_NAME_PRESET,
            },
        };
    }

    protected setBaseProps(subChart: IOfPieChart) {
        super.setBaseProps(subChart);

        // @ts-ignore 饼状图没有坐标轴
        subChart.axisIds = undefined;
        subChart.varyColors = true;
        subChart.secondPieSize = 75;
        subChart.ofPieType = STOfPieType.pie;
        subChart.gapWidth = 100;
        subChart.serLines = [{
            props: {
                style: {
                    border: {
                        linecap: ILineCap.Butt,
                        linejoin: LineJoin.Round,
                        width: 1.5,
                        tdColor: new TDColor({
                            themeKey: STSchemeColorVal.tx1,
                            lumOff: 65000,
                            lumMod: 35000,
                        }),
                    },
                    effect: {},
                },
            },
        }];
    }

    private setOfPieType(subChart: IOfPieChart, type: PresetChartType) {
        if (type === PresetChartType.OF_PIE_BAR) {
            subChart.ofPieType = STOfPieType.bar;
        } else {
            subChart.ofPieType = STOfPieType.pie;
        }
    }
}
